<template>
  <div>
    <a-spin :spinning="loading">
      <a-card v-if="stats || stateData" class="mb-3" :title="title">
        <!-- Header Section - Only show for current stats -->
        <template v-if="!isHistorical">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div class="d-flex">
              <div class="mr-4">
                <a-statistic
                  :title="$t('caisse.type')"
                  :value="$t(`caisse.${stats.type}`)"
                />
              </div>
              <div>
                <a-statistic
                  :title="$t('caisse.balance')"
                  :value="formatCurrency(stats.balance)"
                  :precision="2"
                />
              </div>
            </div>
            <div>
              <a-tag :color="stats.availability === 'OPEN' ? 'green' : 'red'">
                {{
                  stats.availability === "OPEN"
                    ? $t("caisse.ouverte").toUpperCase()
                    : $t("caisse.fermee").toUpperCase()
                }}
              </a-tag>
              <a-tag :color="stats.status === 'active' ? 'blue' : 'default'">
                {{
                  stats.status === "active"
                    ? $t("caisse.actif")
                    : $t("caisse.inactif")
                }}
              </a-tag>
            </div>
          </div>

          <!-- Show caisse name and reference if provided -->
          <div v-if="showName" class="mb-2">
            <h5 class="mb-0">
              {{ stats.name }}
              <small class="text-muted">({{ stats.reference }})</small>
            </h5>
          </div>

          <a-divider />
        </template>

        <!-- Balance Section - Show for historical data -->
        <template v-if="isHistorical">
          <a-statistic
            :title="$t('caisse.balance')"
            :value="formatCurrency(stateData.balance)"
            class="mb-3"
          />
        </template>

        <!-- Debit/Credit Section -->
        <a-row :gutter="16">
          <a-col :span="12">
            <a-statistic
              :title="
                isHistorical ? $t('caisse.debit') : $t('caisse.totalDebits')
              "
              :value="
                formatCurrency(
                  isHistorical ? stateData.debit : stats.state.debit
                )
              "
              :value-style="{ color: '#cf1322' }"
            />
          </a-col>
          <a-col :span="12">
            <a-statistic
              :title="
                isHistorical ? $t('caisse.credit') : $t('caisse.totalCredits')
              "
              :value="
                formatCurrency(
                  isHistorical ? stateData.credit : stats.state.credit
                )
              "
              :value-style="{ color: '#3f8600' }"
            />
          </a-col>
        </a-row>
        <a-divider />

        <!-- Cheques Section -->
        <a-row :gutter="16">
          <a-col :span="12">
            <a-statistic
              :title="$t('caisse.nombreCheques')"
              :value="isHistorical ? stateData.nbCheque : stats.state.nbCheque"
            />
          </a-col>
          <a-col :span="12">
            <a-statistic
              :title="
                isHistorical
                  ? $t('caisse.montantCheques')
                  : $t('caisse.montantTotalCheques')
              "
              :value="
                formatCurrency(
                  isHistorical
                    ? stateData.montantCheque
                    : stats.state.montantCheque
                )
              "
              :precision="2"
            />
          </a-col>
        </a-row>
        <a-divider />
        <!-- Virement Section -->
        <a-row :gutter="16">
          <a-col :span="12">
            <a-statistic
              :title="$t('caisse.nombreVirements')"
              :value="
                isHistorical ? stateData.nbVirement : stats.state.nbVirement
              "
            />
          </a-col>
          <a-col :span="12">
            <a-statistic
              :title="
                isHistorical
                  ? $t('caisse.montantVirements')
                  : $t('caisse.montantTotalVirements')
              "
              :value="
                formatCurrency(
                  isHistorical
                    ? stateData.montantVirement
                    : stats.state.montantVirement
                )
              "
              :precision="2"
            />
          </a-col>
        </a-row>
        <a-divider />
        <!-- Cash Section -->
        <a-row :gutter="16">
          <a-col :span="12">
            <a-statistic
              :title="
                isHistorical
                  ? $t('caisse.nombrePaiementEspeces')
                  : $t('caisse.nombrePaiementEspeces')
              "
              :value="isHistorical ? stateData.nbEspece : stats.state.nbEspece"
            />
          </a-col>
          <a-col :span="12">
            <a-statistic
              :title="
                isHistorical
                  ? $t('caisse.montantEspeces')
                  : $t('caisse.montantTotalPaiementEspeces')
              "
              :value="
                formatCurrency(
                  isHistorical
                    ? stateData.montantEspece
                    : stats.state.montantEspece
                )
              "
              :precision="2"
            />
          </a-col>
        </a-row>
        <a-divider />

        <!-- Traites Section -->
        <a-row :gutter="16">
          <a-col :span="12">
            <a-statistic
              :title="$t('caisse.nombreTraites')"
              :value="isHistorical ? stateData.nbTraite : stats.state.nbTraite"
            />
          </a-col>
          <a-col :span="12">
            <a-statistic
              :title="
                isHistorical
                  ? $t('caisse.montantTraites')
                  : $t('caisse.montantTotalTraites')
              "
              :value="
                formatCurrency(
                  isHistorical
                    ? stateData.montantTraite
                    : stats.state.montantTraite
                )
              "
              :precision="2"
            />
          </a-col>
        </a-row>
        <a-divider />

        <template>
          <a-row :gutter="16">
            <a-col :span="12">
              <a-statistic
                :title="$t('caisse.nombrePaiementTPE')"
                :value="isHistorical ? stateData.nbTPE : stats.state.nbTPE"
              />
            </a-col>
            <a-col :span="12">
              <a-statistic
                :title="$t('caisse.montantTotalPaiementTPE')"
                :value="
                  formatCurrency(
                    isHistorical ? stateData.montantTPE : stats.state.montantTPE
                  )
                "
                :precision="2"
              />
            </a-col>
          </a-row>
          <a-divider />
        </template>

        <!-- Authorized Users Section (only for current stats) -->
        <div
          v-if="
            !isHistorical &&
            stats.authorizedUsers &&
            stats.authorizedUsers.length &&
            showAuthorizedUsers
          "
          class="mb-2"
        >
          <h6 class="mb-2">{{ $t("caisse.utilisateursAutorises") }}</h6>
          <div>
            <div
              v-for="user in stats.authorizedUsers"
              :key="user.user"
              class="mb-1"
            >
              <span class="font-weight-bold mr-2">{{ user.userName }}</span>
              <span>
                <a-tag
                  v-for="perm in user.permissions"
                  :key="perm"
                  color="geekblue"
                  >{{ translatePermission(perm) }}</a-tag
                >
              </span>
            </div>
          </div>
        </div>
      </a-card>
    </a-spin>
  </div>
</template>

<script>
import { formatCurrency as formatCurrencyUtil, getBuildingCurrency } from "@/utilities/countrySpecific";

export default {
  name: "CaisseDetails",
  props: {
    stats: {
      type: Object,
      required: false,
    },
    stateData: {
      type: Object,
      required: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showName: {
      type: Boolean,
      default: false,
    },
    showAuthorizedUsers: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      currency: null,
    };
  },
  computed: {
    isHistorical() {
      return !!this.stateData;
    },
  },
  created() {
    this.currency = getBuildingCurrency();
  },
  methods: {
    formatCurrency(amount) {
      return formatCurrencyUtil(amount, this.currency);
    },
    translatePermission(permission) {
      const permissionMap = {
        OWNER: this.$t("caisse.proprietaireRole"),
        DEPENSE: this.$t("caisse.roleDepense"),
        DEPENSE_ENCAISSEMENT: this.$t("caisse.roleDepenseEncaissement"),
        VIEW: this.$t("caisse.lire"),
        WRITE: this.$t("caisse.ecrire"),
      };
      return permissionMap[permission] || permission;
    },
  },
};
</script>

<style scoped>
.mb-3 {
  margin-bottom: 16px;
}

.mb-2 {
  margin-bottom: 8px;
}

.mb-1 {
  margin-bottom: 4px;
}

.mr-4 {
  margin-right: 16px;
}

.mr-2 {
  margin-right: 8px;
}

.text-muted {
  color: rgba(0, 0, 0, 0.45);
}

.font-weight-bold {
  font-weight: 500;
}
</style>
